/**
 * Rôles auth-service connus de l'Économat : libellés lisibles et catalogue
 * des rôles proposés à la création d'un compte (manuel Économat p.84,
 * « Donner un accès »).
 *
 * Source de vérité des noms : auth.roles (minuscule, cf. migrations
 * auth-service V1, V9 schoolcom/SEDEC, V10 rôles Économat). Le rail que chaque
 * rôle ouvre est décidé par mapRole + ROLE_SECTIONS (lib/auth.tsx) : les
 * libellés ci-dessous décrivent CE QUE LE RÔLE OUVRE réellement, pas un
 * organigramme idéal.
 */

/**
 * Rôles privilégiés : auth-service refuse qu'un appelant non global
 * (ni admin ni super_admin) les attribue (AdminUserResource.PRIVILEGED_ROLES,
 * anti-escalade). On ne les propose donc qu'aux comptes admin / super_admin.
 */
export const PRIVILEGED_ROLES = ["super_admin", "admin", "edaci_admin", "sedec_admin"] as const;

/** Rôles « globaux » côté auth-service (aucune restriction d'attribution). */
export const SCOPE_GLOBAL_ROLES = ["admin", "super_admin"] as const;

/**
 * Archive légale des payeurs (réquisitions) : donation-service n'ouvre
 * GET /api/v1/admin/archives-legales qu'à super_admin et admin. edaci_admin,
 * qui ouvre pourtant le rail complet de l'Économat, n'y a pas accès.
 */
export function canConsultLegalArchives(backendRoles: readonly string[] | undefined): boolean {
  return canGrantPrivilegedRoles(backendRoles);
}

export function canGrantPrivilegedRoles(backendRoles: readonly string[] | undefined): boolean {
  const lower = (backendRoles ?? []).map((r) => r.toLowerCase());
  return lower.some((r) => (SCOPE_GLOBAL_ROLES as readonly string[]).includes(r));
}

export interface RoleOption {
  /** Valeur du formulaire (unique dans la liste déroulante). */
  value: string;
  /** Rôle envoyé à POST /api/auth/admin/users. */
  apiRole: string;
  label: string;
  /** Ce que le rôle ouvre dans l'Économat (aide sous la liste). */
  hint: string;
}

/**
 * Rôles de l'Économat diocésain, sans paroisse de rattachement.
 *
 * « econome » est aussi le rôle de l'économe PAROISSIAL (même nom en base) :
 * l'option diocésaine a donc sa propre valeur de formulaire et envoie
 * « econome » sans paroisse.
 */
export const ECONOMAT_ROLE_OPTIONS: RoleOption[] = [
  { value: "econome_economat", apiRole: "econome", label: "Économe diocésain(e)", hint: "les douze entrées de l'Économat." },
  { value: "adjoint", apiRole: "adjoint", label: "Adjoint(e) à l'économe", hint: "Direction générale, Cabinet, Finances, Péréquation, Paroisses, Séminaires." },
  { value: "cabinet", apiRole: "cabinet", label: "Cabinet de l'Économat", hint: "Cabinet, Direction générale, Communication." },
  { value: "achats", apiRole: "achats", label: "Service des achats", hint: "Centrale d'achats, Procure, Cabinet." },
  { value: "procure", apiRole: "procure", label: "Procure diocésaine", hint: "Procure, Centrale d'achats, Finances, Cabinet." },
  { value: "paroisses", apiRole: "paroisses", label: "Service des paroisses", hint: "Paroisses, Péréquation, Cabinet." },
  { value: "projets", apiRole: "projets", label: "Service des projets", hint: "Projets, Finances, Cabinet." },
  { value: "communication", apiRole: "communication", label: "Service communication", hint: "Communication, Cabinet." },
  { value: "seminaires", apiRole: "seminaires", label: "Service des séminaires", hint: "Séminaires, SEDEC, Cabinet." },
  { value: "sysadmin", apiRole: "sysadmin", label: "Administrateur système (Informatique)", hint: "Administration, Cabinet." },
  { value: "caissier", apiRole: "caissier", label: "Caissier de l'Économat", hint: "Caisse des versements et Reversements des paroisses seulement (encaisse les versements des paroisses à la caisse, imprime les reçus, clôture la caisse), plus Mon compte." },
  { value: "auditeur_diocesain", apiRole: "auditeur_diocesain", label: "Auditeur diocésain (lecture seule)", hint: "SEDEC en lecture seule et l'espace personnel (Cabinet) ; les autres entrées ne lui sont pas encore ouvertes par le serveur." },
];

/** Libellés lisibles de tous les rôles qu'un compte peut porter. */
export const ROLE_LABELS: Record<string, string> = {
  // Économat diocésain
  econome: "Économe",
  adjoint: "Adjoint(e) à l'économe",
  cabinet: "Cabinet de l'Économat",
  achats: "Service des achats",
  procure: "Procure diocésaine",
  paroisses: "Service des paroisses",
  projets: "Service des projets",
  communication: "Service communication",
  seminaires: "Service des séminaires",
  sysadmin: "Administrateur système",
  caissier: "Caissier de l'Économat",
  // Lecture seule du suivi des dons, donnée par l'Économat (20/10/2026).
  suivi_dons: "Suivi des dons (lecture seule)",
  // Diocèse (SEDEC / écoles)
  econome_diocesain: "Économe diocésain (SEDEC)",
  auditeur_diocesain: "Auditeur diocésain",
  sedec_admin: "Administrateur SEDEC",
  // Administration globale
  super_admin: "Super admin",
  admin: "Admin",
  edaci_admin: "Admin EDACI",
  editeur: "Éditeur",
  lecteur: "Lecteur",
  priest: "Prêtre",
  // Paroisses
  cure: "Curé",
  vicaire: "Vicaire",
  secretaire_general: "Secrétaire général",
  secretaire: "Secrétaire",
  responsable_mouvement: "Resp. mouvement",
  conseiller: "Conseiller",
  benevole: "Bénévole",
  user: "Utilisateur",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}
